import { useEffect, useRef } from "preact/hooks";
import type { Signal } from "@preact/signals";

/**
 * Press-and-hold repeat for a stepper button: one step on press, repeating after 400 ms and accelerating from
 * 150 ms down to 40 ms between steps. Pointer capture keeps the repeat going if the finger drifts slightly.
 * Enter / Space step once for keyboard users. No hover behaviour.
 */
export function useRepeat(action: () => void) {
  const timer = useRef<number | null>(null);
  const stop = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);
  return {
    onPointerDown: (e: PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault(); // no focus steal, no text selection, no synthetic click
      try { (e.currentTarget as Element).setPointerCapture?.(e.pointerId); } catch { /* no active pointer: fine */ }
      stop();
      action();
      let n = 0;
      const tick = () => {
        action();
        n++;
        timer.current = window.setTimeout(tick, Math.max(40, 150 * Math.pow(0.85, n)));
      };
      timer.current = window.setTimeout(tick, 400);
    },
    onPointerUp: stop,
    onPointerCancel: stop,
    onLostPointerCapture: stop,
    onContextMenu: (e: Event) => e.preventDefault(),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); action(); }
    },
  };
}

/** − value + with an editable field between large buttons; decimal keypad unless `text` (letters, e.g. "9E"). */
export function Stepper(props: {
  label: string; unit: string; sig: Signal<string>; placeholder?: string; inputId?: string; text?: boolean;
  onStep: (direction: 1 | -1) => void; onBlur: () => void;
}) {
  const dec = useRepeat(() => props.onStep(-1));
  const inc = useRepeat(() => props.onStep(1));
  const id = props.inputId ?? `stepper-${props.label.toLowerCase().replace(/\W+/g, "-")}`;
  return (
    <div class="field">
      <label for={id}>{props.label} ({props.unit})</label>
      <div class="stepper">
        <button type="button" class="step" aria-label={`Decrease ${props.label.toLowerCase()}`} {...dec}>{"−"}</button>
        <input id={id} type="text" inputMode={props.text ? "text" : "decimal"} autocapitalize={props.text ? "characters" : "off"} autocorrect="off" spellcheck={false} autocomplete="off" enterKeyHint="done"
               placeholder={props.placeholder ?? ""} value={props.sig.value}
               onInput={(e) => (props.sig.value = (e.currentTarget as HTMLInputElement).value)}
               onBlur={props.onBlur} />
        <button type="button" class="step" aria-label={`Increase ${props.label.toLowerCase()}`} {...inc}>+</button>
      </div>
    </div>
  );
}
